import Image from 'next/legacy/image';
import { FC, useCallback, useState } from 'react';

import { DsoLineItemProps, Styled } from './';

import Link from 'next/link';
import { useI18n } from 'contexts/i18n';

import { MultiAddressQuantityDistributor } from 'components/DSO/MultiAddressQuantityDistributor';
import { AddressRow } from 'components/DSO/MultiAddressQuantityDistributor/MultiAddressQuantityDistributor.types';
import {
  useDsoAddLineItemsToCart,
  useDsoRemoveLineItemFromCart,
  useDsoUpdateLineItemsToCart,
} from 'reactQuery/mutations/dso';
import { buildDiscountLabel, hasBulkDiscount } from './DSOShoppingCart.helper';
import { isZeroCurrency } from 'utils';
import { DsoCartErrorModal } from 'components/DSO/DsoCartErrorModal';
import { isDsoCartValidationError } from 'components/DSO/DsoCartErrorModal/DsoCartErrorModal.helper';
import { DsoCartValidationError } from 'types/dso/DsoCart.types';

export const DSOLineItemMultiShip: FC<DsoLineItemProps> = ({
  lineItem,
  highlightEmptyAddresses,
  onAddressChange,
  setCheckoutError,
}) => {
  const i18n = useI18n();

  const initialAddressRows: AddressRow[] = lineItem.children.length
    ? lineItem.children.map(child => ({
        quantity: child.quantity.toString(),
        accountNumber: child.shipAccountNumber,
        displayUnitPrice: child.displayUnitPrice,
        displayPrice: child.displayNetTotal,
        displaySubTotal: child.displaySubTotal,
        displayAdjustmentTotal: child.displayAdjustmentTotal,
        discountPercentage: child.discountPercentage,
        displayDiscountTotal: child.displayDiscountTotal,
        childLineItemId: child.childLineItemId,
      }))
    : [
        {
          quantity: '1',
          accountNumber: '',
          displayUnitPrice: lineItem.displayUnitPrice,
          displayPrice: lineItem.displayUnitPrice,
          displaySubTotal: lineItem.displaySubTotal,
          displayAdjustmentTotal: lineItem.displayAdjustmentTotal,
          discountPercentage: '',
        },
      ];
  const [addressRows, setAddressRows] =
    useState<AddressRow[]>(initialAddressRows);
  const [cartError, setCartError] = useState<DsoCartValidationError | null>(
    null
  );

  const syncRowsFromCart = useCallback(
    (cart: { lineItems: (typeof lineItem)[] }) => {
      const updatedLineItem = cart.lineItems.find(
        li => li.spreeVariantId === lineItem.spreeVariantId
      );
      if (!updatedLineItem) return;

      setAddressRows(
        updatedLineItem.children.map(child => ({
          quantity: child.quantity.toString(),
          accountNumber: child.shipAccountNumber,
          displayUnitPrice: child.displayUnitPrice,
          displayPrice: child.displayNetTotal,
          displaySubTotal: child.displaySubTotal,
          displayAdjustmentTotal: child.displayAdjustmentTotal,
          discountPercentage: child.discountPercentage,
          displayDiscountTotal: child.displayDiscountTotal,
          childLineItemId: child.childLineItemId,
        }))
      );
      setCheckoutError([]);
    },
    [lineItem.spreeVariantId]
  );

  const addLineItemsMutation = useDsoAddLineItemsToCart();
  const updateLineItemMutation = useDsoUpdateLineItemsToCart();

  const handleAddRow = useCallback(
    (quantity: string) => {
      addLineItemsMutation.mutate(
        [
          {
            spreeVariantId: lineItem.spreeVariantId.toString(),
            quantity: parseInt(quantity, 10) || 1,
            shipAccountNumber: '',
          },
        ],
        {
          onSuccess: syncRowsFromCart,
          onError: (err: any) => {
            if (isDsoCartValidationError(err?.response?.data)) {
              setCartError(err.response.data);
            }
          },
        }
      );
    },
    [lineItem.spreeVariantId, addLineItemsMutation, syncRowsFromCart]
  );

  const handleRowUpdate = useCallback(
    (row: AddressRow) => {
      if (!row.childLineItemId) return;
      updateLineItemMutation.mutate(
        {
          childLineItemId: row.childLineItemId,
          quantity: parseInt(row.quantity, 10) || 1,
          shipAccountNumber: row.accountNumber,
        },
        {
          onSuccess: syncRowsFromCart,
          onError: (err: any) => {
            if (isDsoCartValidationError(err?.response?.data)) {
              setCartError(err.response.data);
            }
          },
        }
      );
    },
    [updateLineItemMutation, syncRowsFromCart]
  );

  const removeLineItemMutation = useDsoRemoveLineItemFromCart();

  const handleRemoveRow = useCallback(
    (row: AddressRow) => {
      if (!row.childLineItemId) return;
      removeLineItemMutation.mutate(row.childLineItemId, {
        onSuccess: syncRowsFromCart,
      });
    },
    [removeLineItemMutation, syncRowsFromCart]
  );

  const discountPercentage = lineItem.children?.[0]?.discountPercentage;
  const hasDiscount = hasBulkDiscount(discountPercentage);
  const discountLabel = hasDiscount
    ? buildDiscountLabel(discountPercentage ?? '', i18n)
    : '';

  return (
    <>
      <Styled.LineItemContainer
        data-testid="line-item-multiship-container"
        disabled={false}
        $isMultiShip={true}
      >
        <div>
          <Styled.LineItemThumbnail>
            <Link href={`/dso/product/${lineItem.slug}`} passHref>
              <Image
                src={lineItem.imageUrl}
                alt="Prod Image"
                layout="fill"
                objectFit="cover"
                placeholder="blur"
                blurDataURL="data:image/svg+xml;base64,Cjxzdmcgd2lkdGg9IjcwMCIgaGVpZ2h0PSI0NzUiIHZlcnNpb249IjEuMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayI+CiAgPGRlZnM+CiAgICA8bGluZWFyR3JhZGllbnQgaWQ9ImciPgogICAgICA8c3RvcCBzdG9wLWNvbG9yPSIjY2JjYmNiIiBvZmZzZXQ9IjIwJSIgLz4KICAgICAgPHN0b3Agc3RvcC1jb2xvcj0iI2Y5ZjlmOSIgb2Zmc2V0PSI1MCUiIC8+CiAgICAgIDxzdG9wIHN0b3AtY29sb3I9IiNjYmNiY2IiIG9mZnNldD0iNzAlIiAvPgogICAgPC9saW5lYXJHcmFkaWVudD4KICA8L2RlZnM+CiAgPHJlY3Qgd2lkdGg9IjcwMCIgaGVpZ2h0PSI0NzUiIGZpbGw9IiNjYmNiY2IiIC8+CiAgPHJlY3QgaWQ9InIiIHdpZHRoPSI3MDAiIGhlaWdodD0iNDc1IiBmaWxsPSJ1cmwoI2cpIiAvPgogIDxhbmltYXRlIHhsaW5rOmhyZWY9IiNyIiBhdHRyaWJ1dGVOYW1lPSJ4IiBmcm9tPSItNzAwIiB0bz0iNzAwIiBkdXI9IjFzIiByZXBlYXRDb3VudD0iaW5kZWZpbml0ZSIgIC8+Cjwvc3ZnPg=="
              />
            </Link>
          </Styled.LineItemThumbnail>
        </div>
        <Styled.LineItemDescription $isMultiShip>
          <span data-testid="product-name">{lineItem.name}</span>
          <Styled.LineItemAttributes>
            <Styled.DiscountPercentage
              hasDiscount={hasDiscount}
              data-testid="discount-status"
            >
              {discountLabel}
            </Styled.DiscountPercentage>
          </Styled.LineItemAttributes>
        </Styled.LineItemDescription>
        <Styled.LineItemMultiShipHeader>
          <Styled.LineItemTotal
            $isMultiShip
            data-testid="line-item-multiship-total"
          >
            <Styled.LineItemTotalLabel>Total Price:</Styled.LineItemTotalLabel>
            <span data-testid="total-price">
              {lineItem.displayNetTotal}{' '}
              {!isZeroCurrency(lineItem.displayAdjustmentTotal) && (
                <Styled.LineItemTotalWithoutDiscount>
                  {lineItem.displaySubTotal}
                </Styled.LineItemTotalWithoutDiscount>
              )}
            </span>
          </Styled.LineItemTotal>
        </Styled.LineItemMultiShipHeader>

        <Styled.LineItemControlsGrid style={{ marginBottom: 0 }}>
          <Styled.MultiShipColumnHeaders>
            <Styled.LineItemHeaderTitle>
              {i18n['Quantity']}
            </Styled.LineItemHeaderTitle>
            <Styled.LineItemHeaderTitle>
              {i18n['Shipping Address']}
            </Styled.LineItemHeaderTitle>
            <Styled.LineItemHeaderTitle>
              {i18n['Unit Price']}
            </Styled.LineItemHeaderTitle>
            <Styled.LineItemHeaderTitle>
              {i18n['Price']}
            </Styled.LineItemHeaderTitle>
            <Styled.LineItemHeaderTitle></Styled.LineItemHeaderTitle>
          </Styled.MultiShipColumnHeaders>
        </Styled.LineItemControlsGrid>

        <Styled.LineItemControlsGrid style={{ marginTop: 0 }}>
          <MultiAddressQuantityDistributor
            allowableQuantities={lineItem.quantityLimit.map(quantity => ({
              label: quantity.toString(),
              value: quantity.toString(),
            }))}
            addressRows={addressRows}
            setAddressRows={setAddressRows}
            showPricing
            highlightEmptyAddresses={highlightEmptyAddresses}
            onAddressChange={onAddressChange}
            onAddRow={handleAddRow}
            onRowUpdate={handleRowUpdate}
            onRemoveRow={handleRemoveRow}
            allowDeleteFirstRow={true}
            isClearable={false}
            quantityWidth={'100px'}
          />
        </Styled.LineItemControlsGrid>
      </Styled.LineItemContainer>

      <DsoCartErrorModal
        isOpen={cartError !== null}
        error={cartError}
        onClose={() => setCartError(null)}
      />
    </>
  );
};
